import endpoints from "../models/api/api.endpoints";
import api from "../helpers/api.service";
import Helper from "../helpers/helper.service";
import ServiceResponseCode from "../models/service.response.code";
import userService from '../helpers/user.service'
import { redirectToLoginUnauthorized } from '../helpers/base.api.service'

const AUTH_REFRESH_LOCK_KEY = 'auth_refresh_lock'
const REFRESH_LOCK_TTL_MS = 60 * 1000
const REFRESH_PEER_WAIT_MS = 30 * 1000

let refreshInProgress = null

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

function readRefreshLockTimestamp() {
  const raw = window.localStorage.getItem(AUTH_REFRESH_LOCK_KEY)
  if (!raw)
    return null
  const ts = Number(raw)
  return Number.isFinite(ts) ? ts : null
}

function tryAcquireRefreshLock() {
  const now = Date.now()
  const existing = readRefreshLockTimestamp()
  if (existing != null && now - existing < REFRESH_LOCK_TTL_MS)
    return false

  window.localStorage.setItem(AUTH_REFRESH_LOCK_KEY, String(now))
  const after = readRefreshLockTimestamp()
  return after === now
}

function releaseRefreshLock() {
  window.localStorage.removeItem(AUTH_REFRESH_LOCK_KEY)
}

function isRefreshAuthFailure(response) {
  if (!response || response.Success)
    return false

  if (Number(response.HttpCode) === 401 && Number(response.Code) === ServiceResponseCode.TokenAccess)
    return true

  if (Number(response.HttpCode) === 404)
    return true

  return Number(response.Code) === ServiceResponseCode.TokenAccess
}

function isTransientRefreshFailure(response) {
  if (!response)
    return true

  if (response.Error === 'Erro de conexão' || response.Error === 'Timeout')
    return true

  return !response.HttpCode && !response.Code && response.Type === 'error'
}

async function waitForPeerRefresh(requestedBefore) {
  const deadline = Date.now() + REFRESH_PEER_WAIT_MS

  while (Date.now() < deadline) {
    await delay(300)

    const user = userService.getLocalUser(false)
    const requestedAfter = user?.requested ?? 0
    if (user?.accessToken && requestedAfter > requestedBefore)
      return true

    const lockTs = readRefreshLockTimestamp()
    if (lockTs == null || Date.now() - lockTs >= REFRESH_LOCK_TTL_MS)
      return false
  }

  return false
}

export default {
  async login(data) {
    try {
      const url = `${endpoints.authLogin}`;
      const result = await api.login(url, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.Message,
        Success: false,
      }
    }
  },
  async loginExterno(data) {
    try {
      const url = `${endpoints.authLoginExterno}`;
      const result = await api.login(url, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.Message,
        Success: false,
      }
    }
  },
  async loginSso(data) {
    try {
      const url = `${endpoints.authLoginSso}`;
      const result = await api.login(url, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.Message || e.message,
        Success: false,
      }
    }
  },
  async getSsoConfig() {
    try {
      const result = await api.get(endpoints.authSsoConfig, true)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.Message || e.message,
        Success: false,
      }
    }
  },
  async validate(data) {
    try {
      const url = `${endpoints.authValidate}`;
      const result = await api.login(url, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.Message,
        Success: false,
      }
    }
  },
  /**
   * Renova o JWT se já se passaram intervalHours desde o último refresh/login local.
   * Default: 12 horas.
   */
  async refreshTokenIfNeeded(intervalHours = 12) {
    const user = userService.getLocalUser(false)
    if (!user)
      return false

    const lastRefreshMs = user.requested ?? (user.created_at ? user.created_at * 1000 : null)
    if (lastRefreshMs == null)
      return false

    const hoursElapsed = (Date.now() - lastRefreshMs) / (1000 * 60 * 60)
    if (hoursElapsed < intervalHours)
      return false

    return await this.refreshToken()
  },
  async refreshToken() {
    if (refreshInProgress)
      return refreshInProgress

    refreshInProgress = (async () => {
      const userBefore = userService.getLocalUser(false)
      const requestedBefore = userBefore?.requested ?? 0

      if (!tryAcquireRefreshLock()) {
        const peerOk = await waitForPeerRefresh(requestedBefore)
        if (peerOk)
          return true

        if (!tryAcquireRefreshLock()) {
          const retryPeer = await waitForPeerRefresh(requestedBefore)
          return retryPeer
        }
      }

      try {
        const url = endpoints.authRefreshToken
        const result = await api.get(url)
        const response = Helper.getServiceResponse(result)

        if (response.Success) {
          userService.saveUser(response.Data)
          return true
        }

        if (isTransientRefreshFailure(response)) {
          console.warn('refresh token indisponível (rede/timeout), sessão mantida')
          return false
        }

        if (isRefreshAuthFailure(response)) {
          await redirectToLoginUnauthorized(
            response.Message || 'Não autorizado, faça o login novamente'
          )
          return false
        }

        console.error('não foi possível realizar o refresh token')
        return false
      } catch (e) {
        if (String(e?.message || '').includes('auth-redirect'))
          return false

        console.error('erro no refresh token', e)
        return false
      } finally {
        releaseRefreshLock()
        refreshInProgress = null
      }
    })()

    return refreshInProgress
  },
  async Logout() {
    try {
      const url = endpoints.authLogout
      const result = await api.post(url);
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    } finally {
      userService.destroyUser()
    }
  },
  async forgotPassword(email, modalidade, url) {
    try {
      const data = {
        Email: email,
        Modalidade: modalidade,
        Url: url
      }
      const result = await api.post(endpoints.authForgotPassword, data, true)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    }
  },
  async requestChangePassword(data = undefined) {
    try {
      const result = await api.post(endpoints.authRequestChangePassword, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    }
  },
  async changePassword(Id, Password, PasswordConfirmacao, TokenAlterarSenha = null) {

    try {
      const data = {
        Id: Id,
        Password: Password,
        PasswordConfirmacao: PasswordConfirmacao,
        TokenAlterarSenha
      }
      const result = await api.post(endpoints.authChangePassword, data, (TokenAlterarSenha) ? true : false)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    }

  },
  async logUsuario(url, method, body, headers) {
    try {
      const data = {
        dados: {
          url: url,
          method: method,
          body: body,
          headers: headers
        }
      }
      const result = await api.post(endpoints.logUsuario, data)
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    }
  },
  async alterarIdiomaSelecionado(localizacao) {
    try {
      const result = await api.put(endpoints.authIdiomaSelecionado, {
        Localizacao: localizacao,
      })
      return Helper.getServiceResponse(result)
    } catch (e) {
      return {
        Code: ServiceResponseCode.CatchAPICall,
        Message: e.message,
        Success: false,
      }
    }
  },

}
